import { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Copy, Check, BookOpen, Monitor, FileOutput, Layers, MousePointer, Zap } from 'lucide-react';
import { Button } from '../ui/button';

const WORKFLOW_SECTIONS = [
  {
    id: 'setup',
    title: 'Project Setup in Chief Architect',
    icon: Monitor,
    steps: [
      { title: 'Create an AI Data Layer', desc: 'In Chief Architect, create a dedicated layer called "AI_DATA" or "EXPORT_CLEAN". This layer strips decorative fills, hatches, and textures that confuse AI extraction. Keep only walls, dimensions, labels, and room names.' },
      { title: 'Set Consistent Units', desc: 'Use feet-inches (imperial) throughout. Ensure your dimension defaults match: primary = feet-inches, secondary = none. This prevents mixed-unit exports that break automated takeoffs.' },
      { title: 'Name Every Room', desc: 'Use the Room Specification dialog to name each room clearly (e.g., "Primary Bedroom", "Kitchen", "Garage"). These names carry through to PDF exports and enable room-level takeoff matching in KEY-MADE.' },
      { title: 'Label Key Structural Elements', desc: 'Add text annotations for beam locations, bearing walls, point loads, and header sizes. Use Chief Architect\'s "Structural Member" layer to keep these organized.' },
    ]
  },
  {
    id: 'export',
    title: 'Exporting for KEY-MADE',
    icon: FileOutput,
    steps: [
      { title: 'PDF Export Settings', desc: 'File > Export > PDF. Resolution: 300 DPI minimum (600 DPI for AI extraction). Scale: 1/4" = 1\'-0" for floor plans. Color mode: Black & white line drawing works best for AI parsing. Include dimension text.' },
      { title: 'Plan Views to Export', desc: 'Export these views as separate PDF pages: Floor Plan (each level), Electrical Plan, Framing Plan (if available), Roof Plan, Foundation Plan. Each view should have its own clean export.' },
      { title: 'CAD Detail Export', desc: 'For wall sections, stair details, and structural details: export at 3/4" = 1\'-0" or 1" = 1\'-0" scale. Include dimension strings and material callouts.' },
      { title: 'Elevation Exports', desc: 'Export all four exterior elevations and any critical interior elevations. Scale: 1/4" = 1\'-0". Include height dimensions and material annotations.' },
    ]
  },
  {
    id: 'integration',
    title: 'Using Exports in KEY-MADE',
    icon: Layers,
    steps: [
      { title: 'Upload to Floor Plans', desc: 'Go to your project > Floor Plans. Upload the exported PDFs. The AI will attempt to extract rooms, dimensions, and areas automatically. Review and correct any extraction errors.' },
      { title: 'Room Takeoff Sync', desc: 'After AI extraction, room names and dimensions flow into Room Takeoff. Verify measurements match your Chief Architect model. Add window/door counts manually or use the Opening Count tool.' },
      { title: 'Feed the Design Companion', desc: 'With rooms and dimensions populated, the Design Companion AI tools become much more accurate. Run "Room Analysis" and "Structural Attention" prompts for AI-powered insights.' },
      { title: 'Estimate Integration', desc: 'Dimensions from takeoff flow into the Estimate Builder. Line items auto-populate with areas, linear feet, and quantities from your Chief Architect-sourced data.' },
    ]
  },
  {
    id: 'shortcuts',
    title: 'Chief Architect Shortcuts & Tips',
    icon: Zap,
    steps: [
      { title: 'Quick Dimension Strings', desc: 'Select walls > Build > Dimension > Auto Exterior Dimensions. This adds complete dimension chains in one click. For interiors: Auto Interior Dimensions adds room-by-room dims.' },
      { title: 'Schedule Generation', desc: 'Use Chief Architect\'s built-in schedules (Window, Door, Finish) and export them as CSV. These can be cross-referenced with KEY-MADE\'s material tracking.' },
      { title: 'Camera Views for Client', desc: 'Create 3D camera views at eye level (5\'-6" height). Export as high-res images (1920x1080+) for use in the Studio presentation module or Mood Boards.' },
      { title: 'Layout Page Templates', desc: 'Create a Layout template with your company titleblock. Send views to layout pages for organized multi-page PDF exports that keep consistent scale and annotations.' },
      { title: 'Material List Export', desc: 'Tools > Materials List > Calculate. Export the materials list as CSV. While not directly importable yet, it serves as a reference for estimate line items.' },
    ]
  },
  {
    id: 'best-practices',
    title: 'Best Practices',
    icon: BookOpen,
    steps: [
      { title: 'One Source of Truth', desc: 'Make Chief Architect your dimensional source of truth. When dimensions change, re-export and re-upload to KEY-MADE rather than manually editing takeoff numbers.' },
      { title: 'Version Your Exports', desc: 'Name exports with date stamps: "Smith_FloorPlan_2026-02-15.pdf". KEY-MADE\'s document library tracks versions, so you can always compare against previous uploads.' },
      { title: 'Clean Before Export', desc: 'Before exporting, turn off furniture, appliance, and plant layers. These create visual noise that reduces AI extraction accuracy. Keep structural and dimensional layers only.' },
      { title: 'Use Consistent Layer Naming', desc: 'If you manage multiple projects, use the same layer naming convention across all Chief Architect plans. This ensures consistent AI extraction results in KEY-MADE.' },
      { title: 'Structural vs Presentation', desc: 'Keep two display configurations: one "presentation" with full materials/textures for client-facing renders, and one "structural" with clean lines for AI/engineering use.' },
    ]
  }
];

export default function ChiefArchitectGuide({ darkMode = false }) {
  const [expandedSection, setExpandedSection] = useState('setup');
  const [copiedStep, setCopiedStep] = useState(null);

  const bg = darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F8F7F5]';
  const cardBg = darkMode ? 'bg-[#1A1A1E]' : 'bg-white';
  const border = darkMode ? 'border-[#2A2A2A]' : 'border-gray-200';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';

  const copyStep = (content) => {
    navigator.clipboard.writeText(content);
    setCopiedStep(content);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  return (
    <div className={`p-6 space-y-6 ${bg}`} data-testid="chief-architect-guide">
      <div>
        <h2 className={`text-lg font-semibold mb-1 ${text}`}>Chief Architect Workflow</h2>
        <p className={`text-sm ${textMuted}`}>
          End-to-end guide for using Chief Architect with KEY-MADE. From project setup to export to automated takeoff.
        </p>
      </div>

      {/* Quick Reference Card */}
      <div className={`${cardBg} border ${border} rounded-xl p-5`}>
        <h3 className={`text-sm font-semibold mb-3 ${text}`}>Export Quick Reference</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Floor Plans', setting: '300 DPI, 1/4"=1\'-0", B&W' },
            { label: 'Details', setting: '600 DPI, 3/4"=1\'-0"' },
            { label: 'Elevations', setting: '300 DPI, 1/4"=1\'-0"' },
            { label: '3D Views', setting: '1920x1080, Full Color' },
          ].map(item => (
            <div key={item.label} className={`p-3 rounded-lg ${darkMode ? 'bg-[#2A2A2E]' : 'bg-gray-50'}`}>
              <div className={`text-xs font-medium ${text}`}>{item.label}</div>
              <div className={`text-[10px] ${textMuted} mt-1`}>{item.setting}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Workflow Sections */}
      <div className="space-y-2">
        {WORKFLOW_SECTIONS.map((section, sIdx) => {
          const Icon = section.icon;
          const isExpanded = expandedSection === section.id;
          return (
            <div key={section.id} className={`${cardBg} border ${border} rounded-xl overflow-hidden`}>
              <button
                onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left"
                data-testid={`ca-section-${section.id}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-[#BC5D41]/10' : 'bg-[#BC5D41]/5'}`}>
                  <Icon className="w-4 h-4 text-[#BC5D41]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${text}`}>{section.title}</div>
                  <div className={`text-[11px] ${textMuted}`}>{section.steps.length} steps</div>
                </div>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${darkMode ? 'bg-[#2A2A2E] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                  {sIdx + 1}
                </div>
                {isExpanded ? <ChevronDown className={`w-4 h-4 ${textMuted}`} /> : <ChevronRight className={`w-4 h-4 ${textMuted}`} />}
              </button>
              {isExpanded && (
                <div className={`px-5 pb-5 border-t ${border} pt-4 space-y-3`}>
                  {section.steps.map((step, i) => (
                    <div key={i} className={`flex gap-3 p-3 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'} group`}>
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#BC5D41] text-white text-[10px] flex items-center justify-center font-bold mt-0.5">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-semibold ${text} mb-1`}>{step.title}</div>
                        <div className={`text-[11px] ${textMuted} leading-relaxed`}>{step.desc}</div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyStep(`${step.title}: ${step.desc}`); }}
                        className={`flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${textMuted} hover:text-[#BC5D41]`}
                        title="Copy step"
                      >
                        {copiedStep === `${step.title}: ${step.desc}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
